/**
 * @fileoverview Input/output schemas for the story-image-creator-fal-v2 agent.
 *
 * This agent generates story scene images using the fal.ai provider.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.imagePrompt.schema)
  .extend({
    referenceImageUris: z.array(z.string()).optional().describe('An array of Base64 encoded image data URIs to use as reference.'),
    overrideModel: z.string().optional().describe("An optional model identifier to override the agent's default."),
    // Legacy param - kept for backward compatibility
    enableSafetyChecker: z.boolean().optional().default(false).describe('Enable or disable the FAL safety checker. Defaults to false.'),
    // New synthetic params for generalized image generation
    aspectRatio: z.enum(['square', 'portrait', 'landscape', 'wide', 'tall']).optional().describe('Normalized aspect ratio. Maps to model-specific formats.'),
    quality: z.enum(['draft', 'standard', 'high']).optional().describe('Quality level. Maps to resolution or inference steps depending on model.'),
    numberOfImages: z.number().min(1).max(4).optional().describe('Number of images to generate (1-4).'),
    outputFormat: z.enum(['png', 'jpeg', 'webp']).optional().describe('Output image format.'),
  });

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  imageDataUri: z.string().describe('The generated image, returned as a Base64-encoded data URI.'),
  seed: z.number().optional().describe('The seed used for this generation, if returned by the model.'),
  timings: z.object({ inference: z.number().optional(), total: z.number().optional() }).optional().describe('Generation timing data in ms.'),
});

export type Output = z.infer<typeof OutputSchema>;
